import { parseFileToTrack } from './parse-file';
import type { ParsedFileResult } from './parse-file';
import type { ParseRequest, ParseResponse } from './parse.worker';

/**
 * One way to ask for a file to be parsed, whether or not there is a worker.
 *
 * The fallback is not defensive padding. Workers are absent in jsdom, which is
 * where the unit tests run, and they are blocked outright by some corporate
 * policies and by a `file://` page — a pilot who saved the app to a USB stick
 * should still be able to import a flight, just with a busier main thread.
 */
export interface ParseClient {
  parse(filename: string, bytes: Uint8Array, importedAt?: string): Promise<ParsedFileResult>;
  /** Whether work is actually going off-thread. Surfaced in the import panel. */
  readonly offThread: boolean;
  dispose(): void;
}

function inlineClient(): ParseClient {
  return {
    offThread: false,
    parse: (filename, bytes, importedAt) =>
      Promise.resolve(
        parseFileToTrack(filename, bytes, {
          ...(importedAt === undefined ? {} : { importedAt }),
        }),
      ),
    dispose: () => undefined,
  };
}

function workerClient(worker: Worker): ParseClient {
  let nextId = 0;
  const pending = new Map<string, (result: ParsedFileResult) => void>();

  worker.onmessage = (event: MessageEvent<ParseResponse>) => {
    const resolve = pending.get(event.data.id);
    if (resolve !== undefined) {
      pending.delete(event.data.id);
      resolve(event.data.result);
    }
  };

  /*
   * A worker that dies takes every in-flight job with it — most likely the
   * browser killing it for memory on a very large file. Each waiter is
   * answered with a failure rather than left hanging, because a queue row stuck
   * on "parsing" forever is the worst of the available outcomes.
   */
  worker.onerror = (event: ErrorEvent) => {
    for (const [id, resolve] of pending) {
      pending.delete(id);
      resolve({
        ok: false,
        filename: '',
        message: 'The importer stopped unexpectedly while reading this file.',
        detail: [event.message],
      });
    }
  };

  return {
    offThread: true,
    parse: (filename, bytes, importedAt) => {
      nextId += 1;
      const id = String(nextId);
      return new Promise<ParsedFileResult>((resolve) => {
        pending.set(id, (result) => {
          // The worker cannot know the filename when it fails early, so it is
          // restored here where it is known.
          resolve(result.filename === '' ? { ...result, filename } : result);
        });

        // A fresh buffer, because the caller's view may be onto a larger one
        // and transferring it would detach bytes that are not ours.
        const copy = bytes.slice();
        const request: ParseRequest = {
          id,
          filename,
          bytes: copy.buffer,
          ...(importedAt === undefined ? {} : { importedAt }),
        };
        worker.postMessage(request, [copy.buffer]);
      });
    },
    dispose: () => {
      worker.terminate();
      pending.clear();
    },
  };
}

/** A worker-backed client where possible, an in-process one where not. */
export function createParseClient(): ParseClient {
  if (typeof Worker === 'undefined') {
    return inlineClient();
  }
  try {
    return workerClient(
      new Worker(new URL('./parse.worker.ts', import.meta.url), {
        type: 'module',
        name: 'track-parser',
      }),
    );
  } catch {
    return inlineClient();
  }
}
